// src/components/admin/UserLoginDiagnostic.jsx
import React, { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { FiSearch, FiUser, FiDatabase, FiAlertCircle, FiCheckCircle, FiXCircle } from 'react-icons/fi';

/**
 * User login diagnostic tool for troubleshooting authentication issues
 * Runs in browser with proper Firebase authentication
 */
const UserLoginDiagnostic = () => {
  const [email, setEmail] = useState('Christheale@live.co.uk');
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  const runDiagnostic = async () => {
    setIsRunning(true);
    setDiagnosticResults(null);

    const results = {
      email,
      timestamp: new Date().toISOString(),
      environment: 'healehub-dev', // Current environment
      checks: {
        firestoreUser: { status: 'pending', data: null, error: null },
        tenantData: { status: 'pending', data: null, error: null },
        consistency: { status: 'pending', issues: [] }
      },
      recommendations: []
    };

    try {
      // Check 1: Firestore users collection
      console.log('🔍 Checking Firestore users collection...');
      try {
        const usersQuery = query(collection(db, 'users'), where('email', '==', email));
        const usersSnapshot = await getDocs(usersQuery);
        
        if (usersSnapshot.empty) {
          results.checks.firestoreUser.status = 'not_found';
          results.recommendations.push('User document missing from Firestore users collection');
        } else {
          const userDoc = usersSnapshot.docs[0];
          results.checks.firestoreUser.status = 'found';
          results.checks.firestoreUser.data = { id: userDoc.id, ...userDoc.data() };
        }
      } catch (error) {
        results.checks.firestoreUser.status = 'error';
        results.checks.firestoreUser.error = error.message;
        results.recommendations.push(`Users collection access error: ${error.message}`);
      }

      // Check 2: Tenants collection
      console.log('🔍 Checking tenants collection...');
      try {
        const tenantsQuery = query(collection(db, 'tenants'), where('adminEmail', '==', email));
        const tenantsSnapshot = await getDocs(tenantsQuery);
        
        if (tenantsSnapshot.empty) {
          results.checks.tenantData.status = 'not_found';
          results.recommendations.push('User not found as admin in any tenant');
        } else {
          const tenantDoc = tenantsSnapshot.docs[0];
          results.checks.tenantData.status = 'found';
          results.checks.tenantData.data = { id: tenantDoc.id, ...tenantDoc.data() };
        }
      } catch (error) {
        results.checks.tenantData.status = 'error';
        results.checks.tenantData.error = error.message;
        results.recommendations.push(`Tenants collection access error: ${error.message}`);
      }

      // Check 3: Data consistency
      console.log('🔍 Checking data consistency...');
      const user = results.checks.firestoreUser.data;
      const tenant = results.checks.tenantData.data;

      if (user && tenant) {
        if (user.clinicId !== tenant.id) {
          results.checks.consistency.issues.push(`Clinic ID mismatch: user has ${user.clinicId}, tenant is ${tenant.id}`);
        }
        if (!user.role) {
          results.checks.consistency.issues.push('User missing role field');
        }
        if (tenant.status !== 'active') {
          results.checks.consistency.issues.push(`Tenant status is ${tenant.status}, should be active`);
        }
        results.checks.consistency.status = results.checks.consistency.issues.length === 0 ? 'consistent' : 'issues_found';
      } else {
        results.checks.consistency.status = 'incomplete_data';
      }

      // Generate specific recommendations
      if (results.checks.firestoreUser.status === 'error' && 
          results.checks.tenantData.status === 'error' &&
          results.checks.firestoreUser.error?.includes('permissions')) {
        results.recommendations.unshift('LIKELY CAUSE: User data exists in production environment, not development');
        results.recommendations.push('Try logging into production HealeHub instead');
        results.recommendations.push('Check Firebase Console for user in healehub-production project');
      }

    } catch (error) {
      console.error('Diagnostic failed:', error);
      results.error = error.message;
    }

    setDiagnosticResults(results);
    setIsRunning(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'found':
      case 'consistent':
        return <FiCheckCircle className="text-green-500" />;
      case 'not_found':
      case 'error':
      case 'issues_found':
        return <FiXCircle className="text-red-500" />;
      case 'incomplete_data':
        return <FiAlertCircle className="text-yellow-500" />;
      default:
        return <FiAlertCircle className="text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'found':
      case 'consistent':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'not_found':
      case 'error':
      case 'issues_found':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'incomplete_data':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="p-8 space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">User Login Diagnostic</h2>
        <p className="text-gray-600 mb-6">
          Diagnose login issues by checking user data across Firebase collections.
        </p>
        
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isRunning}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter user email to diagnose"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={runDiagnostic}
              disabled={isRunning || !email}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSearch />
              {isRunning ? 'Diagnosing...' : 'Run Diagnostic'}
            </button>
          </div>
        </div>
        
        <div className="text-sm text-gray-500">
          Current Environment: <span className="font-mono bg-gray-100 px-2 py-1 rounded">healehub-dev</span>
        </div>
      </div>
      
      {diagnosticResults && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Diagnostic Results</h3>
          
          {/* Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">
              <strong>Email:</strong> {diagnosticResults.email}<br />
              <strong>Environment:</strong> {diagnosticResults.environment}<br />
              <strong>Timestamp:</strong> {new Date(diagnosticResults.timestamp).toLocaleString()}
            </div>
          </div>
          
          {/* Checks */}
          <div className="space-y-4 mb-6">
            <h4 className="font-medium text-gray-900">Data Checks</h4>
            
            {/* Firestore User Check */}
            <div className={`p-4 rounded-lg border ${getStatusColor(diagnosticResults.checks.firestoreUser.status)}`}>
              <div className="flex items-start gap-3">
                {getStatusIcon(diagnosticResults.checks.firestoreUser.status)}
                <div className="flex-1">
                  <div className="font-medium">Firestore Users Collection</div>
                  <div className="text-sm mt-1">
                    Status: {diagnosticResults.checks.firestoreUser.status}
                  </div>
                  {diagnosticResults.checks.firestoreUser.error && (
                    <div className="text-sm mt-2 font-mono bg-white p-2 rounded">
                      Error: {diagnosticResults.checks.firestoreUser.error}
                    </div>
                  )}
                  {diagnosticResults.checks.firestoreUser.data && (
                    <div className="text-sm mt-2">
                      <strong>Found:</strong> Role: {diagnosticResults.checks.firestoreUser.data.role}, 
                      Clinic ID: {diagnosticResults.checks.firestoreUser.data.clinicId || 'Not set'}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Tenant Data Check */}
            <div className={`p-4 rounded-lg border ${getStatusColor(diagnosticResults.checks.tenantData.status)}`}>
              <div className="flex items-start gap-3">
                {getStatusIcon(diagnosticResults.checks.tenantData.status)}
                <div className="flex-1">
                  <div className="font-medium">Tenants Collection</div>
                  <div className="text-sm mt-1">
                    Status: {diagnosticResults.checks.tenantData.status}
                  </div>
                  {diagnosticResults.checks.tenantData.error && (
                    <div className="text-sm mt-2 font-mono bg-white p-2 rounded">
                      Error: {diagnosticResults.checks.tenantData.error}
                    </div>
                  )}
                  {diagnosticResults.checks.tenantData.data && (
                    <div className="text-sm mt-2">
                      <strong>Found:</strong> {diagnosticResults.checks.tenantData.data.name} 
                      (Status: {diagnosticResults.checks.tenantData.data.status})
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Consistency Check */}
            <div className={`p-4 rounded-lg border ${getStatusColor(diagnosticResults.checks.consistency.status)}`}>
              <div className="flex items-start gap-3">
                {getStatusIcon(diagnosticResults.checks.consistency.status)}
                <div className="flex-1">
                  <div className="font-medium">Data Consistency</div>
                  <div className="text-sm mt-1">
                    Status: {diagnosticResults.checks.consistency.status}
                  </div>
                  {diagnosticResults.checks.consistency.issues.length > 0 && (
                    <div className="text-sm mt-2">
                      <strong>Issues:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {diagnosticResults.checks.consistency.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Recommendations */}
          {diagnosticResults.recommendations.length > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Recommendations</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {diagnosticResults.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserLoginDiagnostic;
